import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { requireClient, ensureCrypto, cacheRecoveryKey } from '@/lib/matrix/client'

interface KeyRestoreDialogProps {
  onClose: () => void
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

export function KeyRestoreDialog({ onClose }: KeyRestoreDialogProps) {
  const [recoveryKey, setRecoveryKey] = useState('')
  const [restoring, setRestoring] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const handleRestore = async (e: FormEvent) => {
    e.preventDefault()
    const key = recoveryKey.trim()
    if (!key) return

    setRestoring(true)
    setError(null)
    setResult(null)

    try {
      await ensureCrypto()

      try {
        cacheRecoveryKey(key)
      } catch {
        throw new Error('Invalid recovery key format. Check for typos and try again.')
      }

      const client = requireClient()
      const crypto = client.getCrypto()
      if (!crypto) throw new Error('Encryption could not be initialized')

      const backupCheck = await crypto.checkKeyBackupAndEnable()
      if (!backupCheck) throw new Error('No key backup found on the server')
      const version = backupCheck.backupInfo.version
      if (!version) throw new Error('Backup version not available')

      const backupKeyBase64 = await client.secretStorage.get('m.megolm_backup.v1')
      if (!backupKeyBase64) {
        throw new Error('Backup decryption key not found in Secret Storage. Is the recovery key correct?')
      }

      const backupKey = base64ToUint8Array(backupKeyBase64)
      await crypto.storeSessionBackupPrivateKey(backupKey, version)

      const restoreResult = await crypto.restoreKeyBackup()
      setResult(`Restored ${restoreResult.total} keys. Refresh the page to decrypt messages.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore keys')
    } finally {
      setRestoring(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <svg className="text-[rgb(var(--np-accent))]" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))]">
            Restore Encryption Keys
          </h2>
        </div>

        <p className="text-xs text-[rgb(var(--np-tx-muted))] mb-4">
          Enter your recovery key (security key) to decrypt message history. Spaces are fine.
        </p>

        <form onSubmit={handleRestore} className="space-y-4">
          <div>
            <label className="label-section mb-2 block">Recovery Key</label>
            <textarea
              value={recoveryKey}
              onChange={(e) => setRecoveryKey(e.target.value)}
              placeholder="EsTc aBcD eFgH iJkL ..."
              rows={3}
              disabled={restoring}
              className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2.5 text-sm font-data text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] focus:border-[rgb(var(--np-accent)/0.3)] transition-all resize-none"
            />
          </div>

          {error && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)] px-4 py-2 rounded-r">
              <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>
            </div>
          )}

          {result && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-ok))] bg-[rgb(var(--np-status-ok)/0.05)] px-4 py-2 rounded-r">
              <p className="text-xs text-[rgb(var(--np-status-ok))]">{result}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={onClose} disabled={restoring}>
              {result ? 'Done' : 'Cancel'}
            </Button>
            {!result && (
              <Button type="submit" disabled={restoring || !recoveryKey.trim()}>
                {restoring ? (
                  <span className="flex items-center gap-2">
                    <Spinner size={14} />
                    Restoring...
                  </span>
                ) : (
                  'Restore Keys'
                )}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
